import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface IDocs {
  doc: string;
  doc_name: string;
  doc_id: number;
  reupload: boolean;
  isEdited?: boolean;
}

interface GenericOrderOption {
  value: string;
  label: string;
}

/** A saved payee as the order wizard's picker renders it. */
export type BeneficiaryRow = {
  id?: string | number;
  name?: string;
  city?: string;
  country?: string;
  bank_name?: string;
  bank_addr?: string;
  bank_country?: string;
  swift_code?: string;
  account_number?: string;
  routing_number?: string;
  beneficiaryType?: string;
  [key: string]: unknown;
};

type CreateOrderStatetype = {
  order_id: string;
  purpose_id: string;
  /** Flywire, WesternUnion, CIBC … which decides the beneficiary form. */
  purpose_mode: string;
  showerror: boolean;
  customer_name: string;
  customer_email: string;
  customer_address: string;
  customer_phone: string;
  customer_disclosers_Ids: number[];
  country: string;
  amount: string;
  currency: string;
  type: string;
  customers_id: string;
  Foreign_Bank_Charges: string;
  customer_rate: string;
  PAN: string;
  pan_found: boolean;
  /** Was typed string[], which it never held, so every field the picker
   *  reads off a row looked like a type error. */
  beneficiary_list: BeneficiaryRow[];
  beneficiary_type: string;
  beneficiary_name: string;
  beneficiary_addr_line_1: string;
  beneficiary_addr_line_2: string;
  beneficiary_city: string;
  beneficiary_state: string;
  beneficiary_country: string;
  beneficiary_zip: string;
  beneficiary_account_number: string;
  beneficiary_bank_name: string;
  beneficiary_bank_addr: string;
  beneficiary_bank_country: string;
  beneficiary_swift_code: string;
  beneficiary_routing_number: string;
  beneficiary_transit_code: string;
  beneficiary_bsbcode: string;
  beneficiary_short_code: string;
  beneficiary_national_id: string;
  beneficiary_iban_no: string;
  beneficiary_reference_number: string;
  beneficiary_correspondant_bank_name: string;
  beneficiary_correspondant_bank_no: string;
  created_by: string;
  discount: string;
  add_on_rates: number;
  docs: object;
  cal_total_charges: string | number;
  cal_gst: string | number;
  cal_tt_charges: string | number;
  cal_total_payable: string;
  /** The order sells below cost. Allowed, but the panel has to say so. */
  at_a_loss: boolean;
  loss_amount: number | null;
  cal_foreign_bank_charges: string | number;
  cal_amount_inr: string;
  cal_amount_currency: string;
  cal_your_rate: string | number;
  cal_customer_rate: string;
  cal_profit: string;
  discloser_amount: string | number;
  tcs: string;
  walkin_order: boolean;
  deal_id: string | number;
  foreign_bank_rates: string | number;
  tt_rates: string | number;
  service_partner_id: string | number;
  settlement_rate: string | number;
  required_docs: string | number;
  fx_charges: string;
  l1_commission: string | number;
  l2_commission: string | number;
  l3_commission: string | number;
  contactsId: string;
  opportunityId: string;
  isStudent: boolean;
  relationship: string;
  passport: File | null;
  studentName: string;
  passportValidity: string;
  kyc_docs: {
    id: string;
    file: File | null;
  }[];
  kyc_doc_base64: {
    [key: string]: string;
  };
  additional_docs: {
    id: string;
    file: File | null;
  }[];
  additional_doc_base64: {
    [key: string]: string;
  };
  is_student: boolean;
  allDocs: IDocs[];
  isNameMismatch: boolean;
  is_deal_flow_Check: boolean;
  selected_beneficiarie_row_id: string | null;
  selected_table_beneficiary_row_id: string | null;
  selected_beneficiary_id: string;
  newGenricOrderID: GenericOrderOption[];
  extra_detail: Record<string, string | number | boolean>;
  studentPassportNumber: string;
};

export const initialState: CreateOrderStatetype = {
  studentPassportNumber: "",
  extra_detail: {},
  order_id: "",
  customer_disclosers_Ids: [],
  isNameMismatch: false,
  isStudent: false,
  relationship: "",
  passport: null,
  studentName: "",
  passportValidity: "",
  allDocs: [],
  kyc_docs: [],
  kyc_doc_base64: {},
  additional_docs: [],
  additional_doc_base64: {},
  contactsId: "",
  opportunityId: "",
  purpose_id: "",
  purpose_mode: "",
  country: "",
  showerror: false,
  customer_name: "",
  customer_email: "",
  customer_address: "",
  customer_phone: "",
  amount: "",
  customer_rate: "",
  type: "beneficiary_receives",
  currency: "",
  customers_id: "",
  Foreign_Bank_Charges: "BEN",
  PAN: "",
  pan_found: false,
  beneficiary_list: [],
  beneficiary_type: "",
  beneficiary_name: "",
  beneficiary_addr_line_1: "",
  beneficiary_addr_line_2: "",
  beneficiary_city: "",
  beneficiary_state: "",
  beneficiary_country: "",
  beneficiary_zip: "",
  beneficiary_account_number: "",
  beneficiary_bank_name: "",
  beneficiary_bank_addr: "",
  beneficiary_bank_country: "",
  beneficiary_swift_code: "",
  beneficiary_routing_number: "",
  beneficiary_reference_number: "",
  beneficiary_transit_code: "",
  beneficiary_bsbcode: "",
  beneficiary_short_code: "",
  beneficiary_national_id: "",
  beneficiary_iban_no: "",
  beneficiary_correspondant_bank_name: "",
  beneficiary_correspondant_bank_no: "",
  created_by: "",
  discount: "",
  add_on_rates: 0,
  docs: {},
  cal_total_charges: "",
  cal_gst: "",
  cal_tt_charges: "",
  cal_total_payable: "",
  at_a_loss: false,
  loss_amount: null,
  cal_foreign_bank_charges: "",
  cal_amount_inr: "",
  cal_amount_currency: "",
  cal_your_rate: "",
  cal_profit: "",
  cal_customer_rate: "",
  discloser_amount: "",
  tcs: "",
  walkin_order: false,
  deal_id: "",
  foreign_bank_rates: "",
  tt_rates: "",
  service_partner_id: "",
  settlement_rate: "",
  required_docs: "",
  fx_charges: "",
  l1_commission: "",
  l2_commission: "",
  l3_commission: "",
  is_student: false,
  is_deal_flow_Check: false,
  selected_beneficiarie_row_id: null,
  selected_table_beneficiary_row_id: null,
  selected_beneficiary_id: "",
  newGenricOrderID: []
};

export const CreateOrderSlice = createSlice({
  name: "CreateOrder",
  initialState,
  reducers: {
    setOrderId: (state, action: PayloadAction<string>) => {
      state.order_id = action.payload;
    },
    setContactId: (state, action: PayloadAction<string>) => {
      state.contactsId = action.payload;
    },
    setOpportunityId: (state, action: PayloadAction<string>) => {
      state.opportunityId = action.payload;
    },
    setPurposeId: (state, action: PayloadAction<string>) => {
      state.purpose_id = action.payload;
    },
    setPurposeMode: (state, action: PayloadAction<string>) => {
      state.purpose_mode = action.payload;
    },
    setshowerror: (state, action: PayloadAction<boolean>) => {
      state.showerror = action.payload;
    },
    setCustomerPhone: (state, action: PayloadAction<string>) => {
      state.customer_phone = action.payload;
    },
    setCustomerAddress: (state, action: PayloadAction<string>) => {
      state.customer_address = action.payload;
    },
    setcustomername: (state, action: PayloadAction<string>) => {
      state.customer_name = action.payload;
    },
    setcustomeremail: (state, action: PayloadAction<string>) => {
      state.customer_email = action.payload;
    },
    setCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
    },
    setAmount: (state, action: PayloadAction<string>) => {
      state.amount = action.payload;
    },
    setCustomer_rate: (state, action: PayloadAction<string>) => {
      state.customer_rate = action.payload;
    },
    setType: (state, action: PayloadAction<string>) => {
      state.type = action.payload;
    },
    setCustomers_id: (state, action: PayloadAction<string>) => {
      state.customers_id = action.payload;
    },
    setcustomer_disclosers_Ids: (state, action: PayloadAction<number[]>) => {
      state.customer_disclosers_Ids = action.payload;
    },
    setForeign_Bank_Charges: (state, action: PayloadAction<string>) => {
      state.Foreign_Bank_Charges = action.payload;
    },
    setPAN: (state, action: PayloadAction<string>) => {
      state.PAN = action.payload;
    },
    setPanFound: (state, action: PayloadAction<boolean>) => {
      state.pan_found = action.payload;
    },
    setBeneficiaryList: (state, action: PayloadAction<BeneficiaryRow[]>) => {
      state.beneficiary_list = action.payload;
    },
    setBeneficiaryType: (state, action: PayloadAction<string>) => {
      state.beneficiary_type = action.payload;
    },
    setBeneficiaryName: (state, action: PayloadAction<string>) => {
      state.beneficiary_name = action.payload;
    },
    setBeneficiaryAddrLine1: (state, action: PayloadAction<string>) => {
      state.beneficiary_addr_line_1 = action.payload;
    },
    setBeneficiaryAddrLine2: (state, action: PayloadAction<string>) => {
      state.beneficiary_addr_line_2 = action.payload;
    },
    setBeneficiaryCity: (state, action: PayloadAction<string>) => {
      state.beneficiary_city = action.payload;
    },
    setBeneficiaryState: (state, action: PayloadAction<string>) => {
      state.beneficiary_state = action.payload;
    },
    setBeneficiaryCountry: (state, action: PayloadAction<string>) => {
      state.beneficiary_country = action.payload;
    },
    setBeneficiaryZip: (state, action: PayloadAction<string>) => {
      state.beneficiary_zip = action.payload;
    },
    setBeneficiaryAccountNumber: (state, action: PayloadAction<string>) => {
      state.beneficiary_account_number = action.payload;
    },
    setBeneficiaryBankName: (state, action: PayloadAction<string>) => {
      state.beneficiary_bank_name = action.payload;
    },
    setBeneficiaryBankAddr: (state, action: PayloadAction<string>) => {
      state.beneficiary_bank_addr = action.payload;
    },
    setBeneficiaryBankCountry: (state, action: PayloadAction<string>) => {
      state.beneficiary_bank_country = action.payload;
    },
    setBeneficiarySwiftCode: (state, action: PayloadAction<string>) => {
      state.beneficiary_swift_code = action.payload;
    },
    setBeneficiaryRoutingNumber: (state, action: PayloadAction<string>) => {
      state.beneficiary_routing_number = action.payload;
    },
    setBeneficiaryTransitCode: (state, action: PayloadAction<string>) => {
      state.beneficiary_transit_code = action.payload;
    },
    setBeneficiaryBsbcode: (state, action: PayloadAction<string>) => {
      state.beneficiary_bsbcode = action.payload;
    },
    setBeneficiaryShortCode: (state, action: PayloadAction<string>) => {
      state.beneficiary_short_code = action.payload;
    },
    setBeneficiaryNationalId: (state, action: PayloadAction<string>) => {
      state.beneficiary_national_id = action.payload;
    },
    setBeneficiaryIbanNo: (state, action: PayloadAction<string>) => {
      state.beneficiary_iban_no = action.payload;
    },
    setBeneficiaryCorrespondantBankName: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.beneficiary_correspondant_bank_name = action.payload;
    },
    setBeneficiaryCorrespondantBankNo: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.beneficiary_correspondant_bank_no = action.payload;
    },
    setCreatedBy: (state, action: PayloadAction<string>) => {
      state.created_by = action.payload;
    },
    setDiscount: (state, action: PayloadAction<string>) => {
      state.discount = action.payload;
    },
    setAddOnRates: (state, action: PayloadAction<number>) => {
      state.add_on_rates = action.payload;
    },
    setCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload;
    },
    setDocs: (state, action: PayloadAction<any>) => {
      if (action.payload === null) {
        setDocs({});
      } else {
        const { filename, file } = action.payload;

        if (filename == null) {
          state.docs = {};
        } else {
          state.docs = { ...state.docs, [filename]: file };
        }
      }
    },
    setcal_total_charges: (state, action: PayloadAction<string | number>) => {
      state.cal_total_charges = action.payload;
    },
    setcal_gst: (state, action: PayloadAction<string | number>) => {
      state.cal_gst = action.payload;
    },
    setcal_tt_charges: (state, action: PayloadAction<string | number>) => {
      state.cal_tt_charges = action.payload;
    },
    setcal_total_payable: (state, action: PayloadAction<string>) => {
      state.cal_total_payable = action.payload;
    },
    setAtALoss: (
      state,
      action: PayloadAction<{ atALoss: boolean; loss: number | null }>,
    ) => {
      state.at_a_loss = action.payload.atALoss;
      state.loss_amount = action.payload.loss;
    },
    setcal_foreign_bank_charges: (
      state,
      action: PayloadAction<string | number>,
    ) => {
      state.cal_foreign_bank_charges = action.payload;
    },
    setcal_amount_inr: (state, action: PayloadAction<string>) => {
      state.cal_amount_inr = action.payload;
    },
    setcal_amount_currency: (state, action: PayloadAction<string>) => {
      state.cal_amount_currency = action.payload;
    },
    setcal_your_rate: (state, action: PayloadAction<string | number>) => {
      state.cal_your_rate = action.payload;
    },
    setcal_profit: (state, action: PayloadAction<string>) => {
      state.cal_profit = action.payload;
    },
    setcal_customer_rate: (state, action: PayloadAction<string>) => {
      state.cal_customer_rate = action.payload;
    },
    setDiscloserAmount: (state, action: PayloadAction<string>) => {
      state.discloser_amount = action.payload;
    },
    setTcs: (state, action: PayloadAction<string>) => {
      state.tcs = action.payload;
    },
    setWalkinOrder: (state, action: PayloadAction<boolean>) => {
      state.walkin_order = action.payload;
    },
    setDealID: (state, action: PayloadAction<string | number>) => {
      state.deal_id = action.payload;
    },
    setForeign_Bank_Rates: (state, action: PayloadAction<string | number>) => {
      state.foreign_bank_rates = action.payload;
    },
    setTTRates: (state, action: PayloadAction<string | number>) => {
      state.tt_rates = action.payload;
    },
    setServicePartnerID: (state, action: PayloadAction<string | number>) => {
      state.service_partner_id = action.payload;
    },
    setBeneficiaryReferencenumber: (state, action: PayloadAction<string>) => {
      state.beneficiary_reference_number = action.payload;
    },
    setSettlementRate: (state, action: PayloadAction<string | number>) => {
      state.settlement_rate = action.payload;
    },
    setRequiredDocs: (state, action: PayloadAction<string | number>) => {
      state.required_docs = action.payload;
    },
    setFXCharges: (state, action: PayloadAction<string>) => {
      state.fx_charges = action.payload;
    },
    setL1CommissionRate: (state, action: PayloadAction<string | number>) => {
      state.l1_commission = action.payload;
    },
    setL2CommissionRate: (state, action: PayloadAction<string | number>) => {
      state.l2_commission = action.payload;
    },
    setSelected_beneficiary_id: (state, action: PayloadAction<string>) => {
      state.selected_beneficiary_id = action.payload;
    },
    setL3CommissionRate: (state, action: PayloadAction<string | number>) => {
      state.l3_commission = action.payload;
    },
    setIsStudent: (state, action: PayloadAction<boolean>) => {
      state.isStudent = action.payload;
    },
    setIsDealflowCheck: (state, action: PayloadAction<boolean>) => {
      state.is_deal_flow_Check = action.payload;
    },
    setRelationship: (state, action: PayloadAction<string>) => {
      state.relationship = action.payload;
    },
    setPassport: (state, action: PayloadAction<File | null>) => {
      state.passport = action.payload;
    },
    setStudentName: (state, action: PayloadAction<string>) => {
      state.studentName = action.payload;
    },
    
    setPassportValidity: (state, action: PayloadAction<string>) => {
      state.passportValidity = action.payload;
    },
    setKycDocs: (
      state,
      action: PayloadAction<{ id: string; file: File | null }[]>,
    ) => {
      state.kyc_docs = action.payload;
    },
    setKycDocBase64: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ) => {
      state.kyc_doc_base64 = action.payload;
    },
    setAdditionalDocs: (
      state,
      action: PayloadAction<{ id: string; file: File | null }[]>,
    ) => {
      state.additional_docs = action.payload;
    },
    setAdditionalDocBase64: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ) => {
      state.additional_doc_base64 = action.payload;
    },
    setAllDocs: (state, action: PayloadAction<IDocs[]>) => {
      state.allDocs = action.payload;
    },
    setIsNameMismatch: (state, action: PayloadAction<boolean>) => {
      state.isNameMismatch = action.payload;
    },
    setSelectedBeneficiarieRowId: (state, action: PayloadAction<string>) => {
      state.selected_beneficiarie_row_id = action.payload || "";
    },
    setSelectedTableBeneficiaryRowId: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.selected_table_beneficiary_row_id = action.payload || "";
    },
    setNewGenricOrderID: (state, action: PayloadAction<GenericOrderOption[]>) => {
      state.newGenricOrderID = action.payload;
    },
    setExtraDetail: (state, action: PayloadAction<Record<string, string | number | boolean>>) => {
      state.extra_detail = action.payload;
    },
    setStudentPassportNumber: (state, action: PayloadAction<string>) => {
      state.studentPassportNumber = action.payload;
    },
    resetState: () => {
      return initialState;
    },
  },
});

export const {
  setStudentPassportNumber,
  setExtraDetail,
  setIsNameMismatch,
  setPurposeId,
  setPurposeMode,
  setshowerror,
  setcustomername,
  setcustomeremail,
  setCustomerPhone,
  setCustomerAddress,
  setCountry,
  setAmount,
  setType,
  setCustomers_id,
  setForeign_Bank_Charges,
  setPAN,
  setPanFound,
  setBeneficiaryList,
  setBeneficiaryType,
  setBeneficiaryName,
  setBeneficiaryAddrLine1,
  setBeneficiaryAddrLine2,
  setBeneficiaryCity,
  setBeneficiaryState,
  setBeneficiaryCountry,
  setBeneficiaryZip,
  setBeneficiaryAccountNumber,
  setBeneficiaryBankName,
  setBeneficiaryBankAddr,
  setBeneficiaryBankCountry,
  setBeneficiarySwiftCode,
  setBeneficiaryRoutingNumber,
  setBeneficiaryTransitCode,
  setBeneficiaryBsbcode,
  setBeneficiaryShortCode,
  setBeneficiaryNationalId,
  setBeneficiaryIbanNo,
  setBeneficiaryCorrespondantBankName,
  setBeneficiaryCorrespondantBankNo,
  setCreatedBy,
  setDiscount,
  setAddOnRates,
  setCurrency,
  setDocs,
  setcal_total_charges,
  setcal_gst,
  setcal_tt_charges,
  setcal_total_payable,
  setAtALoss,
  setcal_amount_currency,
  setcal_profit,
  setcal_foreign_bank_charges,
  setcal_amount_inr,
  setcal_your_rate,
  setcal_customer_rate,
  setDiscloserAmount,
  setTcs,
  setCustomer_rate,
  setWalkinOrder,
  setDealID,
  setForeign_Bank_Rates,
  setTTRates,
  setServicePartnerID,
  setSettlementRate,
  setRequiredDocs,
  setFXCharges,
  setL1CommissionRate,
  setL2CommissionRate,
  setL3CommissionRate,
  setContactId,
  setOpportunityId,
  setIsStudent,
  setRelationship,
  setPassport,
  setStudentName,
  setPassportValidity,
  setKycDocs,
  setKycDocBase64,
  setAdditionalDocs,
  setAdditionalDocBase64,
  setAllDocs,
  setBeneficiaryReferencenumber,
  setIsDealflowCheck,
  setSelectedBeneficiarieRowId,
  setSelectedTableBeneficiaryRowId,
  setSelected_beneficiary_id,
  setcustomer_disclosers_Ids,
  setOrderId,
  setNewGenricOrderID,
  resetState
} = CreateOrderSlice.actions;

export default CreateOrderSlice.reducer;
